<template>
  <div v-if="showReconnectSection" class="zalo-reconnect-section">
    <div class="connection-status">
      <div class="status-header">
        <h3>{{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.CONNECTION_STATUS') }}</h3>
        <div class="status-indicator" :class="connectionStatusClass">
          {{ connectionStatusText }}
        </div>
      </div>
      
      <div v-if="!isConnected" class="reconnect-actions">
        <woot-button
          color-scheme="primary"
          :loading="isReconnecting"
          @click="startReconnection"
        >
          {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.RECONNECT') }}
        </woot-button>
      </div>
    </div>

    <!-- Reconnect Modal -->
    <woot-modal
      v-if="showReconnectModal"
      :show="showReconnectModal"
      :on-close="closeReconnectModal"
    >
      <div class="reconnect-modal">
        <!-- Modal Header -->
        <div class="modal-header text-center mb-6">
          <h2 class="text-xl font-semibold text-slate-800">
            {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.RECONNECT_TITLE') }}
          </h2>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-8">
          <woot-loading-state :message="statusMessage" />
        </div>

        <!-- QR Code Section -->
        <div v-else-if="qrCodeData" class="qr-section text-center">
          <h3 class="text-lg font-medium text-slate-700 mb-4">
            {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.SCAN_QR') }}
          </h3>
          
          <div class="qr-wrapper flex justify-center mb-6">
            <div class="qr-container bg-white p-4 rounded-lg shadow-lg border-2 border-slate-200">
              <img :src="qrCodeData" alt="QR Code" class="qr-code max-w-48 h-auto" />
            </div>
          </div>
          
          <div class="status-info">
            <div class="status-indicator inline-flex items-center px-4 py-2 rounded-full text-sm font-medium" :class="qrStatusClass">
              {{ qrStatusText }}
            </div>
          </div>
        </div>

        <!-- Modal Actions -->
        <div class="modal-actions flex justify-center mt-8">
          <woot-button
            color-scheme="secondary"
            @click="closeReconnectModal"
            class="px-6 py-2"
          >
            {{ $t('GENERAL.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import axios from 'axios';

export default {
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isReconnecting: false,
      showReconnectModal: false,
      qrCodeData: '',
      integrationId: '',
      accessToken: '',
      isLoading: false,
      statusMessage: '',
      qrStatus: 'waiting_qr_scan',
      qrStatusInterval: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    showReconnectSection() {
      return this.inbox.channel_type === 'Channel::ZaloPersonal';
    },
    sessionData() {
      return this.getSessionData();
    },
    isConnected() {
      return this.sessionData.integration_id && this.sessionData.is_healthy === true;
    },
    connectionStatusClass() {
      return this.isConnected ? 'status-connected' : 'status-disconnected';
    },
    connectionStatusText() {
      return this.isConnected 
        ? this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.CONNECTED')
        : this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.DISCONNECTED');
    },
    qrStatusClass() {
      const statusMap = {
        waiting_qr_scan: 'status-waiting',
        qr_scanned: 'status-scanned',
        logged_in: 'status-success',
        login_failed: 'status-failed',
      };
      return statusMap[this.qrStatus] || 'status-waiting';
    },
    qrStatusText() {
      const statusMap = {
        waiting_qr_scan: this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_STATUS.WAITING'),
        qr_scanned: this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_STATUS.SCANNED'),
        logged_in: this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_STATUS.SUCCESS'),
        login_failed: this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_STATUS.FAILED'),
      };
      return statusMap[this.qrStatus] || '';
    },
    microserviceUrl() {
      return window.hichatConfig?.zaloPersonalMicroserviceUrl || 'http://localhost:8000';
    },
  },
  beforeUnmount() {
    if (this.qrStatusInterval) {
      clearInterval(this.qrStatusInterval);
    }
  },
  methods: {
    getSessionData() {
      try {
        return JSON.parse(this.inbox.session_data || '{}');
      } catch (error) {
        return {};
      }
    },

    showAlert(message, options = { type: 'info' }) {
      const normalizedOptions =
        typeof options === 'string' ? { type: options } : options || {};
      useAlert(message, { type: 'info', ...normalizedOptions });
    },
    
    async startReconnection() {
      try {
        this.isReconnecting = true;
        this.isLoading = true;
        this.statusMessage = 'Đang khởi tạo kết nối lại...';
        this.showReconnectModal = true;

        const response = await axios.post(`${this.microserviceUrl}/api/register`, {
          hichat_account_id: this.currentAccountId.toString(),
          hichat_inbox_id: this.inbox.id.toString(),
          hichat_api_key: this.currentUser.access_token,
          hichat_base_url: window.location.origin,
          agent_id: this.currentUser.id.toString(),
        });

        if (response.data.success) {
          this.qrCodeData = response.data.qr_code;
          this.integrationId = response.data.integration_id;
          this.accessToken = response.data.access_token;
          this.statusMessage = response.data.message || 'Vui lòng quét mã QR';
          this.startQRStatusCheck();
        } else {
          throw new Error(response.data.message || 'Đăng ký thất bại');
        }
      } catch (error) {
        this.showAlert(
          error.message || 'Có lỗi xảy ra khi khởi tạo kết nối lại',
          { type: 'error' }
        );
        this.closeReconnectModal();
      } finally {
        this.isReconnecting = false;
        this.isLoading = false;
      }
    },

    startQRStatusCheck() {
      this.qrStatusInterval = setInterval(async () => {
        try {
          const response = await axios.get(
            `${this.microserviceUrl}/api/qr-status/${this.integrationId}`
          );
          
          if (response.data.success) {
            // Nếu response trả về integration_id thì cập nhật lại
            if (response.data.integration_id) {
              this.integrationId = response.data.integration_id;
            }
            this.qrStatus = response.data.status;
            
            if (response.data.status === 'logged_in' || response.data.is_connected) {
              clearInterval(this.qrStatusInterval);
              if (response.data.access_token) {
                this.accessToken = response.data.access_token;
              }
              await this.updateConnection();
            } else if (response.data.status === 'new_qr' && response.data.qr_code) {
              this.qrCodeData = response.data.qr_code;
            } else if (response.data.status === 'login_failed') {
              clearInterval(this.qrStatusInterval);
              this.showAlert('Đăng nhập thất bại. Vui lòng thử lại.', {
                type: 'error',
              });
              this.closeReconnectModal();
            }
          }
        } catch (error) {
          // Continue checking
        }
      }, 2000);
    },

    async updateConnection() {
      try {
        const sessionData = {
          integration_id: this.integrationId,
          access_token: this.accessToken,
          is_healthy: true,
          last_health_check: new Date().toISOString(),
        };

        const currentSessionData = this.getSessionData();
        const mergedSessionData = {
          ...currentSessionData,
          ...sessionData,
          reconnected_at: new Date().toISOString()
        };

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            session_data: JSON.stringify(mergedSessionData)
          }
        });

        await axios.post(
          `${this.microserviceUrl}/api/integration/${this.integrationId}/update`,
          { hichat_inbox_id: this.inbox.id.toString() },
          { headers: { 'X-Access-Token': this.accessToken } }
        );

        await this.$store.dispatch('inboxes/get');

        this.showAlert('Kết nối lại thành công!', { type: 'success' });
        this.closeReconnectModal();
        this.$emit('connection-updated');
      } catch (error) {
        console.error('Reconnect error:', error);
        this.showAlert(error.message || 'Có lỗi khi cập nhật kết nối', {
          type: 'error',
        });
      }
    },

    closeReconnectModal() {
      this.showReconnectModal = false;
      this.qrCodeData = '';
      this.integrationId = '';
      this.accessToken = '';
      this.qrStatus = 'waiting_qr_scan';
      if (this.qrStatusInterval) {
        clearInterval(this.qrStatusInterval);
      }
    },
  },
};
</script>

<style scoped>
.zalo-reconnect-section {
  margin: 1.5rem 0;
  padding: 1.5rem;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  background: var(--color-background);
}

.connection-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-body);
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 6px;
  font-size: 0.625rem;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.status-connected {
  background-color: var(--g-100);
  color: var(--g-700);
}

.status-disconnected {
  background-color: var(--r-100);
  color: var(--r-700);
}

.reconnect-modal {
  width: 500px;
  max-width: 90vw;
}

.modal-header {
  margin-bottom: 1.5rem;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-body);
}

.qr-section {
  text-align: center;
  margin-bottom: 1.5rem;
}

/* Modal Layout Improvements */
.reconnect-modal {
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
}

.qr-wrapper {
  margin: 1rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-container {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
}


.qr-code {
  max-width: 200px;
  height: auto;
  border-radius: 8px;
  display: block;
}

.status-info {
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;
}

/* Status Indicators */
.status-waiting {
  background-color: #dbeafe;
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.status-scanned {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

.status-success {
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #6ee7b7;
}

.status-failed {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

/* Connection Status */
.status-connected {
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #6ee7b7;
}

.status-disconnected {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.modal-actions {
  text-align: right;
  margin-top: 1.5rem;
}
</style> 